import React from 'react';
import { Loader2, AlertTriangle, X } from 'lucide-react';
import { useTranslation, Trans } from 'react-i18next';

interface SubscriptionCancelModalProps {
    isOpen: boolean;
    onClose: () => void;
    onConfirm: () => void;
    isLoading: boolean;
    endDate: string;
}

const SubscriptionCancelModal: React.FC<SubscriptionCancelModalProps> = ({
    isOpen,
    onClose,
    onConfirm,
    isLoading,
    endDate
}) => {
    const { t } = useTranslation('billing');
    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm !m-0">
            <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden animate-in fade-in zoom-in duration-200 border-t-4 border-red-500">
                <div className="p-6">
                    <div className="flex justify-between items-start mb-6">
                        <div className="flex items-center gap-3">
                            <div className="p-3 rounded-xl bg-red-50 text-red-600">
                                <AlertTriangle size={24} />
                            </div>
                            <div>
                                <h3 className="text-lg font-bold text-slate-900">{t('modals.cancel_subscription.title')}</h3>
                                <p className="text-sm text-slate-500">{t('modals.cancel_subscription.subtitle')}</p>
                            </div>
                        </div>
                        <button onClick={onClose} disabled={isLoading} className="text-slate-400 hover:text-slate-600 transition-colors">
                            <X size={24} />
                        </button>
                    </div>

                    <div className="bg-red-50 rounded-xl p-4 border border-red-100 mb-6 text-red-800 text-sm">
                        <p className="font-bold mb-1">{t('modals.cancel_subscription.warning_title')}</p>
                        <p><Trans i18nKey="modals.cancel_subscription.warning_body" ns="billing" values={{ date: endDate }} components={{ strong: <strong /> }} /></p>
                    </div>

                    <div className="flex gap-3">
                        <button
                            onClick={onClose}
                            disabled={isLoading}
                            className="flex-1 py-3 px-4 bg-white border border-slate-200 text-slate-700 font-bold rounded-xl hover:bg-slate-50 transition-colors disabled:opacity-50"
                        >
                            {t('modals.cancel_subscription.keep_plan')}
                        </button>
                        <button
                            onClick={onConfirm}
                            disabled={isLoading}
                            className="flex-1 py-3 px-4 bg-red-600 text-white font-bold rounded-xl hover:bg-red-700 transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
                        >
                            {isLoading ? <Loader2 size={18} className="animate-spin" /> : null}
                            {isLoading ? t('modals.cancel_subscription.processing') : t('modals.cancel_subscription.confirm')}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SubscriptionCancelModal;
